/**
 * /byop/callback — Pollinations BYOP redirect target.
 *
 * Sprint 28.17 — PR-3a Settings + BYOP. After the user approves access
 * on enter.pollinations.ai, Pollinations redirects here with a URL
 * fragment of the shape `#api_key=sk_…&state=…` (success) or
 * `#error=access_denied&state=…` (denial). We:
 *
 *   1. Read window.location.hash on mount.
 *   2. Parse it via `parseCallbackFragment` (pure helper, tested in
 *      `byop.test.ts`).
 *   3. Verify the returned `state` matches the CSRF token we stashed
 *      in sessionStorage before the redirect. Mismatch → reject and
 *      surface an error.
 *   4. On success: `byopStore.connect(apiKey, scope)` — stores the
 *      key in sessionStorage (NOT localStorage) so it expires on tab
 *      close.
 *   5. Navigate back to /settings#providers regardless of outcome —
 *      success shows the connected pill; denial / mismatch shows an
 *      error banner.
 *
 * Logic stays minimal because the heavy lifting lives in
 * `apps/web/src/lib/byop.ts` (parsing) and
 * `apps/web/src/state/byopStore.ts` (storage). This file just
 * orchestrates the side effects.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  parseCallbackFragment,
  isValidSecretKey,
  BYOP_CSRF_KEY,
} from '../lib/byop.ts';
import { useByopStore } from '../state/byopStore.ts';

type CallbackOutcome =
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

export function ByopCallbackPage() {
  const navigate = useNavigate();
  const connect = useByopStore((s) => s.connect);
  const [outcome, setOutcome] = useState<CallbackOutcome>({ kind: 'pending' });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const expectedState = sessionStorage.getItem(BYOP_CSRF_KEY) ?? '';
    // Always clear the CSRF token on mount — it's single-use.
    sessionStorage.removeItem(BYOP_CSRF_KEY);

    const result = parseCallbackFragment(window.location.hash);

    if (result.kind === 'invalid') {
      setOutcome({
        kind: 'error',
        message:
          'Pollinations did not return an api_key in the redirect. Please try connecting again.',
      });
      return;
    }
    if (result.kind === 'denied') {
      setOutcome({
        kind: 'error',
        message: 'You declined to connect. No worries — you can try again any time.',
      });
      return;
    }
    // Success path — verify CSRF state.
    if (!expectedState || result.state !== expectedState) {
      setOutcome({
        kind: 'error',
        message:
          'Security check failed (state token mismatch). Please reconnect from Settings.',
      });
      return;
    }
    if (!isValidSecretKey(result.apiKey)) {
      setOutcome({
        kind: 'error',
        message: 'Pollinations returned an unexpected key shape. Please try again.',
      });
      return;
    }
    connect(result.apiKey);
    setOutcome({ kind: 'success' });

    // Brief pause so the user sees the success message, then back to
    // Settings where the connection pill is now visible.
    const t = window.setTimeout(() => {
      navigate('/settings#providers', { replace: true });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [connect, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        background: 'var(--surface-primary, #0a0a0a)',
        color: 'var(--text-primary, #f8f8f8)',
        textAlign: 'center',
        gap: '12px',
      }}
      role="region"
      aria-label="Pollinations connect callback"
      data-testid="byop-callback"
    >
      <h1 style={{ fontSize: 18, margin: 0 }}>
        {outcome.kind === 'pending' && 'Connecting your Pollinations account…'}
        {outcome.kind === 'success' && '🌼 Connected — using your pollen for AI gen'}
        {outcome.kind === 'error' && 'Connection failed'}
      </h1>
      {outcome.kind === 'error' && (
        <p style={{ maxWidth: 480, fontSize: 14, opacity: 0.85, margin: 0 }}>
          {outcome.message}
        </p>
      )}
      <Link
        to="/settings#providers"
        style={{ fontSize: 13, color: 'var(--text-link, #4ea1ff)' }}
      >
        ← Back to Settings
      </Link>
    </div>
  );
}

